import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { UrlEnum } from 'src/app/shared/enum/url.enum';
import {
  CSRMatchingConfigModel,
  CSRMatchingConfigUpdateModel,
} from 'src/app/shared/model/corporate/corporate-csr-matching-config.model';
import {
  MatchingRequestConfirmModel,
  PledgingSubmissionModel,
} from 'src/app/shared/model/csr/external-csr-donation.model';
import { HttpRequestService } from '../../http-request/http-request.service';
import { LocalStorageService } from '../../local-storage/local-storage.service';
import { UtilitiesService } from '../../utilities/utilities.service';

@Injectable({
  providedIn: 'root',
})
export class CorporateCsrService {
  private corporateAdminCsrUrl = `${UrlEnum.SHOHAY_CORPORATE}/csr-ngo-campaign-mapping`;
  private corporateAllowedCsrUrl = `${UrlEnum.SHOHAY_CORPORATE}/allowed-csr-ngo-campaign`;
  private externalDonationUrl = `${UrlEnum.SHOHAY_CORPORATE}/external-csr`;
  private corporateAnalyticsUrl = `${UrlEnum.SHOHAY_CORPORATE}/external-csr/summary`;
  private corporateTransactionUrl = `${UrlEnum.SHOHAY_CORPORATE}/transaction`;
  private corporatePledgingUrl = `${UrlEnum.SHOHAY_CORPORATE}/corporate-pledging`;

  constructor(
    private readonly utilitiesService: UtilitiesService,
    private readonly httpRequestService: HttpRequestService,
    private readonly localStorageService: LocalStorageService
  ) {}

  public getAllCsrNgos(): Observable<any> {
    return this.httpRequestService
      .get(`${this.corporateAllowedCsrUrl}/get-all`)
      .pipe(
        map((x: any) => this.utilitiesService.responseHandler(x)),
        map((x: any) => {
          return x
            ? x
                .filter((item) => item.type === 'NGO')
                .map((e: any) => ({
                  ...e,
                  logo_url: this.utilitiesService.jsonParse(e?.logo_url)?.url,
                }))
            : [];
        })
      );
  }

  public getAllCsrCampaigns(): Observable<any> {
    return this.httpRequestService
      .get(`${this.corporateAllowedCsrUrl}/get-all`)
      .pipe(
        map((x: any) => this.utilitiesService.responseHandler(x)),
        map((x: any) => {
          return x
            ? x
                .filter((item) => item.type === 'CAMPAIGN')
                .map((e: any) => ({
                  ...e,
                  cover_img_vdo: this.utilitiesService.jsonParse(
                    e?.cover_img_vdo
                  )?.url,
                }))
            : [];
        })
      );
  }

  public removeAllowedNgoCampaign(id: string): Observable<any> {
    return this.httpRequestService
      .delete(`${this.corporateAllowedCsrUrl}/delete-by-id/${id}`)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public getCsrNgosByCompany(company_id: string): Observable<any> {
    return this.httpRequestService
      .get(`${this.corporateAdminCsrUrl}/get-by-company/${company_id}`)
      .pipe(
        map((x: any) => this.utilitiesService.responseHandler(x)),
        map((x: any) => {
          return x?.filter((item) => item?.type === 'NGO');
        })
      );
  }

  public getCsrCampaignsByCompany(company_id: string): Observable<any> {
    return this.httpRequestService
      .get(`${this.corporateAdminCsrUrl}/get-by-company/${company_id}`)
      .pipe(
        map((x: any) => this.utilitiesService.responseHandler(x)),
        map((x: any) => {
          return x?.filter((item) => item?.type === 'CAMPAIGN');
        })
      );
  }

  public mapCsrMatchingConfig(
    matchingBody: CSRMatchingConfigModel
  ): Observable<any> {
    return this.httpRequestService
      .post(`${this.corporateAdminCsrUrl}/create`, matchingBody)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public editMatchingData(
    id: string,
    matchingBody: CSRMatchingConfigUpdateModel
  ): Observable<any> {
    return this.httpRequestService
      .patch(`${this.corporateAdminCsrUrl}/update/${id}`, matchingBody)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public stopOrStartCsrMatching(
    id: string,
    body: any,
    startOrStop: 'start' | 'stop'
  ): Observable<any> {
    return this.httpRequestService
      .patch(`${this.corporateAdminCsrUrl}/${startOrStop}/${id}`, body)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public getDonationHistoryByCompany(
    companyId: string,
    month: string
  ): Observable<any> {
    return this.httpRequestService
      .get(
        `${this.externalDonationUrl}/donation/get-by-company-id/${companyId}?donationMonth=${month}`
      )
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public acceptMatchingRequest(
    id: string,
    body: MatchingRequestConfirmModel
  ): Observable<any> {
    return this.httpRequestService
      .patch(
        `${this.externalDonationUrl}/donation/update-request-status/${id}`,
        body
      )
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public getInternalDonationHistoryByUser(userId: string): Observable<any> {
    return this.httpRequestService
      .get(`${this.externalDonationUrl}/donation/get-by-user/${userId}`)
      .pipe(
        map((x: any) => this.utilitiesService.responseHandler(x)),
        map((x: any[]) =>
          x.sort(
            this.utilitiesService.dynamicSortWithDate('created_at', 'desc')
          )
        )
      );
  }

  public getDonationHistoryByUser(userId: string): Observable<any> {
    return this.httpRequestService
      .get(`${this.externalDonationUrl}/summary/get-donation-history/${userId}`)
      .pipe(
        map((x: any) => this.utilitiesService.responseHandler(x)),
        map((x: any[]) =>
          x.sort(
            this.utilitiesService.dynamicSortWithDate('created_at', 'desc')
          )
        )
      );
  }

  public getInternalPledgingHistoryByUser(userId: string): Observable<any> {
    const status = 'PLEDGED';
    return this.httpRequestService
      .get(
        `${this.corporatePledgingUrl}/get-all-by-user-and-status/${userId}?status=${status}`
      )
      .pipe(
        map((x: any) => this.utilitiesService.responseHandler(x)),
        map((x: any[]) =>
          x.sort(
            this.utilitiesService.dynamicSortWithDate('created_at', 'desc')
          )
        )
      );
  }

  public getExternalPledgingHistoryByUser(userId: string): Observable<any> {
    const status = 'PLEDGED';
    return this.httpRequestService
      .get(
        `${this.externalDonationUrl}/pledging/get-all-by-user-and-status/${userId}?status=${status}`
      )
      .pipe(
        map((x: any) => this.utilitiesService.responseHandler(x)),
        map((x: any[]) =>
          x.sort(
            this.utilitiesService.dynamicSortWithDate('created_at', 'desc')
          )
        )
      );
  }

  public submitInternalPledging(
    id: string,
    body: PledgingSubmissionModel
  ): Observable<any> {
    return this.httpRequestService
      .patch(`${this.corporatePledgingUrl}/update-pledging/${id}`, body)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public submitExternalPledging(
    id: string,
    body: PledgingSubmissionModel
  ): Observable<any> {
    return this.httpRequestService
      .patch(`${this.externalDonationUrl}/pledging/update-pledging/${id}`, body)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public getDashboardSummary(companyId: string, id: string): Observable<any> {
    return this.httpRequestService
      .get(
        `${this.externalDonationUrl}/summary/get-dashboard-summary/${companyId}/${id}`
      )
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public getDonationAnalytics(
    ngo_campaign_id: string,
    last_date: number
  ): Observable<any> {
    const companyId = this.localStorageService.getSessionUser()?.company_id;
    return this.httpRequestService
      .get(
        `${this.corporateAnalyticsUrl}/get-donation-analytics?company_id=${companyId}&ngo_campaign_id=${ngo_campaign_id}&last_day_count=${last_date}`
      )
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public geTransactionById(tranId: string): Observable<any> {
    return this.httpRequestService
      .get(`${this.corporateTransactionUrl}/get-by-transaction/${tranId}`)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }
}
